import { Core } from "@faire/design-tokens";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { Address } from "@faire/web--source/ui/Addresses/Display/Address";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import isEmpty from "lodash/isEmpty";
import React from "react";
import styled from "styled-components";

import { useSelectedBrandOrder } from "../hooks/useSelectedBrandOrder";

import { HorizontalLineWithColor } from "./HorizontalLineWithMargin";
import { OrderNumber } from "./OrderNumber";
import { OrderSummaryCardImages } from "./OrderSummaryCardImages";
import { SelectedIssuesBulletedListCard } from "./SelectedIssuesBulletedListCard";
import { UserInfoSummaryCard } from "./UserInfoSummaryCard";

export const OrderSummaryCard: React.FC = () => {
  const { selectedBrandOrder } = useSelectedBrandOrder();
  const {
    brand_name,
    status_string,
    brand_commission_string,
    summary_string,
    brand_order,
  } = selectedBrandOrder ?? {};
  const shipTo = brand_order?.shipping_address;
  return (
    <OrderSummaryCardWrapper>
      {!isEmpty(selectedBrandOrder) ? (
        <>
          <OrderSummaryCardSectionWrapper>
            <Row>
              <OrderSummaryCardImages selectedBrandOrder={selectedBrandOrder} />
            </Row>
            <Spacer height="2x" />
            <Typography variant="paragraphSansMedium">{brand_name}</Typography>
            <Spacer height="1x" />
            <OrderNumber />

            {brand_commission_string ? (
              <>
                <Spacer height="1x" />
                <Row>
                  <Typography variant="paragraphSansMedium">
                    <StrictLocalMsg
                      defaultMessage="Commission"
                      description={{
                        text: "This is title of the commission of an order",
                      }}
                    />
                  </Typography>
                  <Spacer width="1/2x" />
                  <Typography>{brand_commission_string}</Typography>
                </Row>
              </>
            ) : null}
            <Spacer height="1x" />
            <Row>
              <Typography variant="paragraphSansMedium">
                <StrictLocalMsg
                  defaultMessage="Total"
                  description={{
                    text: "This is title of the total amount of an order",
                  }}
                />
              </Typography>
              <Spacer width="1/2x" />
              <Typography>{summary_string}</Typography>
            </Row>
            <Spacer height="1x" />
            <Typography variant="paragraphSansMedium">
              {status_string}
            </Typography>
          </OrderSummaryCardSectionWrapper>
          <HorizontalLineWithColor />
          <OrderSummaryCardSectionWrapper>
            <Typography variant="paragraphSansMedium">
              <StrictLocalMsg
                defaultMessage="Ship to"
                description={{
                  text: "Title for displaying shipping to address",
                  img: "https://cdn.faire.com/fastly/430670fe8ecd4f4b577e0e6fa29c41661e0c5ed9d7ce7c86d6c5c2d80486a1c1.png",
                }}
              />
            </Typography>
            <Spacer height="1x" />
            <Address boldFirst={false} address={shipTo} />
          </OrderSummaryCardSectionWrapper>
        </>
      ) : (
        <OrderSummaryCardSectionWrapper>
          <UserInfoSummaryCard />
        </OrderSummaryCardSectionWrapper>
      )}
      <HorizontalLineWithColor />
      <OrderSummaryCardSectionWrapper>
        <SelectedIssuesBulletedListCard />
      </OrderSummaryCardSectionWrapper>
    </OrderSummaryCardWrapper>
  );
};

const OrderSummaryCardWrapper = styled(Column)`
  margin-top: ${getSpacing("5x")};
  background-color: ${Core.surface.muted};
  min-width: 320px;
  max-width: 400px;
  height: fit-content;
  overflow-x: hidden;
  position: sticky;
  position: -webkit-sticky;
  top: 0;
`;

const OrderSummaryCardSectionWrapper = styled(Column)`
padding: ${getSpacing("3x", "2x")};
}`;
